import Pagination from "@/components/common/table/pagination";
import AssignUserDropdown from "@/pages/roles/details/assign-user-dropdown";
import { useApprovalGroupForm } from "./approval-group-context";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState, useCallback } from "react";
import { UserLevelSelector } from "./user-level-selector";
import type {
  ApprovalGroupLevel,
  ApprovalUser,
} from "@/types/entities/approval-groups";

const PAGE_SIZE = 20;

export const GroupUsersList = ({ readonly }: { readonly?: boolean }) => {
  const {
    selectedUsers,
    setSelectedUsers,
    leaderAlreadySelected,
    supervisorAlreadySelected,
  } = useApprovalGroupForm();

  const [page, setPage] = useState(0);

  const localPagination = useMemo(() => {
    return {
      data: selectedUsers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      meta: {
        total: selectedUsers.length,
        page: 1,
        per_page: PAGE_SIZE,
      },
    };
  }, [page, selectedUsers]);

  useEffect(() => {
    const currentData = selectedUsers.slice(
      page * PAGE_SIZE,
      (page + 1) * PAGE_SIZE,
    );
    if (selectedUsers.length > 0 && currentData.length === 0 && page > 0) {
      setPage((p) => p - 1);
    }
  }, [selectedUsers, page]);

  const removeUser = useCallback(
    (id: string) => {
      setSelectedUsers((prev) => prev.filter((user) => user.id !== id));
    },
    [setSelectedUsers],
  );

  const handleLevelChange = useCallback(
    (id: string, level: ApprovalGroupLevel) => {
      setSelectedUsers((prev) =>
        prev.map((user) => (user.id === id ? { ...user, level } : user)),
      );
    },
    [setSelectedUsers],
  );

  const handleAddUser = useCallback(
    (user: ApprovalUser) => {
      setSelectedUsers((prev) => [...prev, user]);
    },
    [setSelectedUsers],
  );

  return (
    <div className="flex flex-col flex-1 overflow-y-auto">
      <div className="flex justify-between items-center p-3 bg-neutral-object border border-b-0 rounded-t-lg border-neutral-quiet">
        <Heading variant="h6">Lista de usuarios</Heading>
        {!readonly && (
          <AssignUserDropdown
            variant="default"
            onAddUser={(v) => {
              handleAddUser({
                id: v.id,
                last_name: v.last_name || "",
                name: v.name || "",
                email: v.email || "",
                full_name: `${v.name} ${v.last_name}`.trim(),
              });
            }}
            selectedUsers={selectedUsers}
            useApprovalGroupsEndpoint={true}
          />
        )}
      </div>
      <div className="border border-neutral-quiet bg-neutral-object-accent-silent h-[4px]"></div>
      <div
        className={cn(
          "bg-neutral-object border border-t-0 rounded-b-lg border-neutral-quiet h-full shadow-custom-lg",
          localPagination.data.length === 0 &&
            "flex flex-col justify-center items-center gap-2",
        )}
      >
        {localPagination.data.length > 0 ? (
          <div className="h-full flex flex-col justify-between">
            <div className="flex flex-col">
              <ul className="overflow-y-auto ">
                {localPagination.data.map(
                  ({ id, name, last_name, level, email }, index) => {
                    const fallback = name ? name.charAt(0).toUpperCase() : "U";
                    const stripper = index % 2 !== 0;
                    return (
                      <div
                        key={`user-${id}`}
                        className={cn(
                          stripper
                            ? "bg-neutral-object-accent-silent"
                            : "bg-neutral-object",
                        )}
                      >
                        <div className="px-3 py-2.5 text-body font-normal text-xs flex items-center justify-between">
                          <div className="flex items-center gap-4 max-w-[70%]">
                            <Avatar size="xs">
                              <AvatarFallback color="action">
                                {fallback}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col w-full">
                              <span className="text-body text-base truncate line-clamp-1">
                                {`${name} ${last_name}`}
                              </span>
                              <span className="text-size-body-xs text-body-quiet truncate line-clamp-1">
                                {email}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <UserLevelSelector
                              id={id}
                              currentLevel={level}
                              readOnly={readonly}
                              onLevelChange={handleLevelChange}
                              leaderAlreadySelected={!leaderAlreadySelected}
                              supervisorAlreadySelected={
                                !supervisorAlreadySelected
                              }
                            />
                            {!readonly && (
                              <Button
                                variant="ghost"
                                size="icon"
                                type="button"
                                aria-label="Eliminar usuario"
                                onClick={() => removeUser(id)}
                              >
                                <Icon type="far" name="times" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </ul>
            </div>
            <div className="w-full px-3 py-4">
              <Pagination
                currentPage={page + 1}
                totalPages={Math.ceil(selectedUsers.length / PAGE_SIZE)}
                hasNext={page + 1 < Math.ceil(selectedUsers.length / PAGE_SIZE)}
                hasPrevious={page > 0}
                onPageChange={(pages) => {
                  setPage(pages - 1); // Adjusting for zero-based index
                }}
                onNext={() => {
                  if (page + 1 < Math.ceil(selectedUsers.length / PAGE_SIZE)) {
                    setPage((prev) => prev + 1);
                  }
                }}
                onPrevious={() => {
                  if (page > 0) {
                    setPage((prev) => prev - 1);
                  }
                }}
                pageSize={localPagination.data.length}
                totalItems={localPagination.meta.total}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center gap-2">
            <Heading variant="h5" color="quiet" className="text-center">
              No se han añadido usuarios a este grupo
            </Heading>
            <p className="text-size-body text-body-quiet text-center">
              Añade usuarios a la lista para asignar al grupo de aprobación
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
